import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Briefcase,
  MapPin,
  Phone,
  Globe,
  Mail,
  ExternalLink,
  PenLine,
  Info,
} from "lucide-react";

function Row({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-3 text-sm text-slate-700">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <span className="break-words">{children}</span>
    </div>
  );
}

export default function DetailCard({ item, open, onOpenChange, onWriteEmail }) {
  if (!item) return null;
  const isJob = item.type === "jobad";
  const normalizedSite =
    item.website && !item.website.startsWith("http")
      ? `https://${item.website}`
      : item.website;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span
              className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                isJob ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
              }`}
            >
              {isJob ? <Briefcase className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate text-left font-display text-lg">
                {item.name}
              </DialogTitle>
              <p className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                {isJob ? "Utannonserat jobb" : "Spontanansökan"}
              </p>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Kontaktuppgifter och åtgärder för {item.name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          {isJob && item.headline && (
            <p className="font-medium text-slate-900">{item.headline}</p>
          )}
          {item.industry && (
            <span className="inline-block rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              {item.industry}
            </span>
          )}

          <div className="space-y-2.5 rounded-xl bg-slate-50 p-3">
            <Row icon={MapPin}>{item.address || item.city}</Row>
            {!isJob && <Row icon={Phone}>{item.phone}</Row>}
            {!isJob && normalizedSite && (
              <Row icon={Globe}>
                <a
                  href={normalizedSite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 underline"
                >
                  {item.website}
                </a>
              </Row>
            )}
            {!isJob && <Row icon={Mail}>{item.email}</Row>}
            {isJob && item.deadline && (
              <Row icon={Info}>Sista ansökningsdag: {item.deadline.slice(0, 10)}</Row>
            )}
          </div>

          {item.description && (
            <p className="jk-scroll max-h-32 overflow-y-auto text-sm leading-relaxed text-slate-600">
              {item.description}
            </p>
          )}

          {!isJob && !item.email && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Ingen e-post finns registrerad här. Du kan ändå skriva ett mail och fylla i
              mottagaren själv, eller kontakta via telefon/webbplats.
            </div>
          )}

          {isJob ? (
            <Button
              asChild
              disabled={!item.apply_url}
              data-testid="apply-link-btn"
              className="w-full gap-2 rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              <a href={item.apply_url || "#"} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" /> Ansök här
              </a>
            </Button>
          ) : (
            <Button
              data-testid={`company-spontaneous-btn-${item.id}`}
              onClick={() => onWriteEmail(item)}
              className="w-full gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700"
            >
              <PenLine className="h-4 w-4" /> Skriv spontanansökan
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
